// ═══════════════════════════════════════════════════════════════════════════════
//  src/interview/InterviewSetup.jsx
//  INTERVIEW ROOM — el formulario previo
//
//  Cuatro decisiones y a la sala. Ni una más.
//
//  ── LAS DOS QUE IMPORTAN ────────────────────────────────────────────────────
//  MODO      Asistido (la IA escribe la respuesta modelo y tú la lees en voz
//            alta) o Libre (tú contestas como puedas y la IA te califica).
//            El asistido entrena la boca; el libre mide la cabeza.
//  DATOS     Perfil prefabricado o tus datos reales. Pegar un CV es fricción
//            real — quien quiere probar el simulador a las once de la noche no
//            lo tiene a la mano. Con un preset entra en un toque; el día que
//            vaya en serio, pega su CV y la vacante a la que aplica.
//
//  El reparto de fases se muestra ANTES de empezar. Que el alumno vea que la
//  técnica llega a la mitad y no al principio cambia cómo entra: sabe que hay
//  calentamiento, igual que en una entrevista real.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';

import { getPresets, startInterview } from './interviewApi';

const MODES = [
  {
    id: 'assisted', icon: '🎧', name: 'Asistido',
    desc: 'La IA escribe la respuesta perfecta y tú la lees en voz alta. Entrena pronunciación y ritmo.',
  },
  {
    id: 'free', icon: '🎤', name: 'Libre',
    desc: 'Contestas con tus propias palabras y la IA te califica como un reclutador.',
  },
];

const SOURCES = [
  {
    id: 'preset', icon: '⚡', name: 'Perfil listo',
    desc: 'Elige un puesto y empieza ahora mismo.',
  },
  {
    // Los seis perfiles precargados son de tecnologia. Un contador, una
    // abogada o un arquitecto no tenian donde entrenar salvo redactando su CV
    // entero — y esa friccion dejaba el simulador fuera de su alcance.
    id: 'role', icon: '✍️', name: 'Otro puesto',
    desc: 'Escribe tu profesion y el sistema arma la entrevista.',
  },
  {
    id: 'real', icon: '📄', name: 'Mis datos',
    desc: 'Pega tu CV y la vacante real a la que aplicas.',
  },
];

// Se muestran como ejemplo para que quede claro que cabe cualquier oficio y no
// solo puestos de oficina.
const ROLE_HINTS = ['Contador', 'Arquitecta', 'Abogado', 'Enfermero', 'Chef', 'Docente'];

const COUNTS = [
  { n: 15, name: 'Rápido', desc: '~20 min' },
  { n: 20, name: 'Estándar', desc: '~30 min' },
  { n: 30, name: 'Maratón', desc: '~45 min' },
];

export default function InterviewSetup({ onStarted, onExit }) {
  const [meta, setMeta] = useState(null);
  const [mode, setMode] = useState('assisted');
  const [source, setSource] = useState('preset');
  const [count, setCount] = useState(15);
  const [presetId, setPresetId] = useState('backend');
  const [roleLabel, setRoleLabel] = useState('');
  const [cv, setCv] = useState('');
  const [vacancy, setVacancy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    getPresets()
      .then((d) => { if (alive) setMeta(d); })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, []);

  const cvOk = cv.trim().length >= 40;
  const roleOk = roleLabel.trim().length >= 3;
  const ready = source === 'preset' || (source === 'role' ? roleOk : cvOk);

  const begin = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await startInterview({
        mode,
        source,
        language: 'en',
        questionCount: count,
        ...(source === 'preset' ? { presetId } : {}),
        ...(source === 'role' ? { roleLabel: roleLabel.trim() } : {}),
        ...(source === 'real'
          ? { roleLabel: roleLabel.trim(), cv: cv.trim(), vacancy: vacancy.trim() }
          : {}),
      });
      onStarted(data);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const plan = meta?.plans?.[count] || [];

  return (
    <div className="iv-screen">
      <header className="iv-hero">
        <div className="iv-tag">🎯 Simulador de entrevistas</div>
        <h1 className="iv-h1">
          <span className="iv-ac">INTERVIEW</span>
          <span className="iv-glow">ROOM</span>
        </h1>
        <p className="iv-lead">
          Una entrevista completa en inglés, con las cinco fases reales y un
          reclutador que no te la pone fácil. Sales con un reporte de dónde te
          quiebras.
        </p>
      </header>

      <div className="iv-form">
        <div className="iv-field">
          <span className="iv-lbl">1 · Cómo quieres entrenar</span>
          <div className="iv-choice">
            {MODES.map((m) => (
              <button
                key={m.id} type="button" className="iv-opt"
                aria-pressed={mode === m.id} onClick={() => setMode(m.id)}
              >
                <span className="iv-opt-icon" aria-hidden="true">{m.icon}</span>
                <b>{m.name}</b>
                <span>{m.desc}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="iv-field">
          <span className="iv-lbl">2 · Con qué datos</span>
          <div className="iv-choice">
            {SOURCES.map((s) => (
              <button
                key={s.id} type="button" className="iv-opt"
                aria-pressed={source === s.id} onClick={() => setSource(s.id)}
              >
                <span className="iv-opt-icon" aria-hidden="true">{s.icon}</span>
                <b>{s.name}</b>
                <span>{s.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {source === 'preset' ? (
          <div className="iv-field">
            <span className="iv-lbl">Puesto</span>
            <div className="iv-choice">
              {(meta?.presets || []).map((p) => (
                <button
                  key={p.id} type="button" className="iv-opt"
                  aria-pressed={presetId === p.id} onClick={() => setPresetId(p.id)}
                >
                  <span className="iv-opt-icon" aria-hidden="true">{p.icon}</span>
                  <b>{p.label}</b>
                </button>
              ))}
            </div>
            <p className="iv-help">
              Cada perfil trae su CV y su vacante ya escritos. Las preguntas salen
              de ahí, así que son las de ese puesto y no genéricas.
            </p>
          </div>
        ) : source === 'role' ? (
          <div className="iv-field">
            <span className="iv-lbl">¿Qué puesto quieres practicar?</span>
            <input
              className="iv-input" type="text" maxLength={60}
              placeholder="Ej. Contador, Arquitecta, Abogado corporativo…"
              value={roleLabel} onChange={(e) => setRoleLabel(e.target.value)}
            />
            <div className="iv-hints">
              {ROLE_HINTS.map((h) => (
                <button
                  key={h} type="button" className="iv-hint"
                  aria-pressed={roleLabel.trim() === h}
                  onClick={() => setRoleLabel(h)}
                >
                  {h}
                </button>
              ))}
            </div>
            <p className="iv-help">
              {roleOk
                ? `Vamos a montarte una entrevista de ${roleLabel.trim()}: el CV y la vacante los escribe el sistema, y las preguntas salen de ahí.`
                : 'Cualquier profesión sirve. No hace falta tu CV — con el nombre del puesto basta.'}
            </p>
          </div>
        ) : (
          <>
            <div className="iv-field">
              <span className="iv-lbl">Puesto al que aplicas</span>
              <input
                className="iv-input" type="text" maxLength={120}
                placeholder="Ej. Backend Engineer en una fintech"
                value={roleLabel} onChange={(e) => setRoleLabel(e.target.value)}
              />
            </div>
            <div className="iv-field">
              <span className="iv-lbl">Tu perfil o CV</span>
              <textarea
                className="iv-area" maxLength={6000}
                placeholder="Pega tu CV o describe tu experiencia: años, tecnologías, proyectos, logros con números…"
                value={cv} onChange={(e) => setCv(e.target.value)}
              />
              <p className="iv-help">
                {cvOk
                  ? `${cv.trim().length} caracteres — suficiente para personalizar las preguntas.`
                  : 'Mínimo unas líneas. Sin esto las preguntas salen genéricas y el entrenamiento no sirve.'}
              </p>
            </div>
            <div className="iv-field">
              <span className="iv-lbl">La vacante <span style={{ textTransform: 'none', letterSpacing: 0 }}>(opcional)</span></span>
              <textarea
                className="iv-area" maxLength={4000}
                placeholder="Pega la descripción del puesto. Con esto el reclutador pregunta justo lo que esa empresa pediría."
                value={vacancy} onChange={(e) => setVacancy(e.target.value)}
              />
            </div>
          </>
        )}

        <div className="iv-field">
          <span className="iv-lbl">3 · Cuántas preguntas</span>
          <div className="iv-choice">
            {COUNTS.map((c) => (
              <button
                key={c.n} type="button" className="iv-opt"
                aria-pressed={count === c.n} onClick={() => setCount(c.n)}
              >
                <b>{c.n} preguntas</b>
                <span>{c.name} · {c.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {plan.length > 0 && (
          <div className="iv-field">
            <span className="iv-lbl">Así va a ir la entrevista</span>
            <div className="iv-plan">
              {plan.map((p) => (
                <div key={p.id} className={`iv-plan-row iv-ph-${p.id}`}>
                  <span>{p.label}</span>
                  <b className="iv-num">{p.questions}</b>
                </div>
              ))}
            </div>
            <p className="iv-help">
              El orden no es casual: un reclutador real calienta antes de atacar
              la técnica, y deja el sueldo para el final.
            </p>
          </div>
        )}

        {error && <div className="iv-fail">{error}</div>}

        <div className="iv-actions">
          <button type="button" className="iv-btn" onClick={begin} disabled={!ready || busy}>
            {busy ? 'Abriendo la sala…' : 'Entrar a la entrevista →'}
          </button>
          <button type="button" className="iv-btn iv-btn-ghost" onClick={onExit}>
            Volver al gimnasio
          </button>
        </div>
      </div>
    </div>
  );
}
